import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { NLPLogo } from '../components/nlp/NLPLogo';
import { ArrowRight, Check, Sparkles, GraduationCap, Users, Building2, Award, Clock, Heart, Briefcase, Trophy, Network, Target, Eye, Lightbulb, Mic, BookOpen } from 'lucide-react';

const masteryModules = [
  {
    icon: Target,
    title: 'Advanced Values Work',
    body: 'Values sit behind an enormous amount of human motivation and behavior. They influence what we pursue, what we avoid, what matters to us, what we\'ll fight for, what we\'ll sacrifice, and sometimes why we repeatedly create outcomes we consciously say we don\'t want.',
    detail: 'You\'ll explore values elicitation, values hierarchies, values conflicts, Values Levels, changing values, how values influence motivation and behavior, how values interact with goals, and applying values within personal and professional contexts. And you\'ll begin exploring their broader applications within leadership, business, sales, teams and organizations.',
    pullquote: 'Instead of merely knowing what someone wants... you begin understanding why it matters to them.',
  },
  {
    icon: Eye,
    title: 'Meta Programs',
    body: 'Why can two people experience exactly the same event and respond completely differently? Why is one person motivated by achieving something while another is motivated by avoiding a problem? Why does one person need options while another prefers procedures?',
    detail: 'Meta Programs help us understand some of the unconscious filters through which people process experience. You\'ll learn how to identify patterns influencing motivation, decision-making, communication, information processing, relationships, leadership, sales, performance and change. And you\'ll explore how Meta Programs can be applied not only with individual clients but within business and organizational environments.',
  },
  {
    icon: BookOpen,
    title: 'Quantum Linguistics',
    body: 'Words don\'t simply describe someone\'s experience. They can reveal its structure.',
    detail: 'You\'ll go beyond basic language patterns and develop greater sophistication in how you listen, question and communicate. You\'ll explore advanced linguistic distinctions including advanced Meta Model applications, Sleight of Mouth patterns, Hierarchy of Ideas, conversational reframing, advanced questioning, changing levels of abstraction and using language to shift perspective.',
    pullquote: 'Sometimes a powerful intervention isn\'t a complicated process. It\'s the right sentence—or the right question—at exactly the right moment.',
  },
  {
    icon: Network,
    title: 'Advanced Strategies & Submodalities',
    body: 'People don\'t simply have behaviors. They have internal processes that produce those behaviors.',
    detail: 'You\'ll go deeper into identifying, understanding and working with the internal strategies people use to make decisions, become motivated, learn, communicate, create emotional responses, buy, sell, lead and take action. You\'ll develop greater flexibility in strategy elicitation and intervention and explore advanced applications of submodalities.',
  },
  {
    icon: Lightbulb,
    title: 'Advanced Modelling',
    body: 'Modelling sits at the heart of NLP. Rather than merely asking successful people what they do, NLP asks a much more interesting question: What patterns make that result possible?',
    detail: 'You\'ll explore advanced modelling and how to identify important elements underlying excellence. How does someone think? What do they pay attention to? What internal strategies do they use? What beliefs support their performance? What linguistic and behavioral patterns are involved? And which elements are essential to reproducing the result?',
  },
  {
    icon: Clock,
    title: 'Master-Level Time Line Therapy®',
    body: 'You were introduced to powerful Time Line Therapy® processes at Practitioner level. Now we\'ll take that work deeper.',
    detail: 'You\'ll develop greater sophistication in working with negative emotions, limiting decisions, limiting beliefs, personal history, values, future creation and Creating Your Future®. And you\'ll learn how these processes can be integrated into a broader change experience rather than simply used as isolated techniques.',
  },
  {
    icon: Heart,
    title: 'The Personal Breakthrough',
    body: 'Imagine having the ability to step back from an individual\'s presenting problem and look at the broader structure of their experience. Their values. Beliefs. Decisions. Emotions. Language. Strategies. Goals. Identity. Patterns. And then design an extended change experience around the person rather than simply the problem.',
    detail: 'You\'ll learn how to structure and facilitate an intensive Personal Breakthrough experience that integrates multiple elements of your training. Working with a training partner, you\'ll facilitate a Personal Breakthrough for another person and then switch — experiencing your own Personal Breakthrough yourself.',
    pullquote: 'Twin Modelling: You experience the process through the eyes of the practitioner and through the experience of the client.',
  },
  {
    icon: Mic,
    title: 'Advanced Hypnosis',
    body: 'Master Practitioner expands your understanding and application of hypnosis.',
    detail: 'You\'ll explore additional approaches to hypnotic communication and develop greater flexibility in working with trance, suggestion and unconscious processes. Depending on the application, we\'ll explore approaches associated with traditions including Ericksonian hypnosis, Elman-style approaches, more direct/authoritarian approaches, permissive approaches, advanced hypnotic phenomena, suggestion and unconscious communication.',
  },
  {
    icon: Users,
    title: 'Advanced Presentation Skills',
    body: 'Knowing something and communicating it effectively are two different skills.',
    detail: 'You\'ll develop advanced presentation and communication capabilities informed by NLP. You\'ll explore how to structure information, engage different types of learners, communicate ideas clearly, use language intentionally, manage attention, present with greater flexibility and design experiences rather than simply deliver information.',
  },
];

const orgApplications = [
  { icon: Briefcase, title: 'Business', body: 'Understand behavioral patterns, goals, communication, motivation and performance within a business context.' },
  { icon: Users, title: 'Leadership', body: 'Explore how values, Meta Programs, communication patterns and decision-making influence leadership.' },
  { icon: Network, title: 'Teams', body: 'Look at differences between people and how those differences can influence communication, motivation, collaboration and conflict.' },
  { icon: Building2, title: 'Organizations', body: 'Begin looking at patterns occurring across groups and systems rather than viewing every challenge purely as an individual problem.' },
];

const journeyIncludes = [
  '12 Live Master Practitioner Training Days',
  'Advanced NLP',
  'Advanced Values Work',
  'Values Levels',
  'Meta Programs',
  'Quantum Linguistics',
  'Sleight of Mouth',
  'Advanced Language Patterns',
  'Advanced Strategies',
  'Advanced Submodalities',
  'Advanced Modelling',
  'Master-Level Time Line Therapy®',
  'Advanced Hypnosis',
  'Personal Breakthrough Methodology',
  'Facilitating a Personal Breakthrough',
  'Receiving Your Own Personal Breakthrough',
  'Twin Modelling',
  'Advanced Presentation Skills',
  'Business Applications',
  'Team Applications',
  'Leadership Applications',
  'Organizational Applications',
  'Advanced New Model of NLP',
  'Advanced NLP OS™ Applications',
  'Ongoing Practice & Integration',
  'Professional Community',
];

export function MastertrackSalesPage() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen bg-nlp-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-nlp-bg/95 backdrop-blur-lg shadow-sm">
        <div className="max-w-[1216px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/home')} className="flex items-center">
              <NLPLogo className="h-12" />
            </button>
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Curriculum
              </button>
              <button onClick={() => document.getElementById('certifications')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Certifications
              </button>
              <button onClick={() => document.getElementById('bonus')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Founding Member Bonus
              </button>
            </nav>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => document.getElementById('bonus')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
              >
                Your Bonus
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-4 md:px-8 bg-nlp-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nlp-olive/5 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-nlp-gold/5 rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />
        <div className="max-w-[900px] mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-nlp-gold-dark" />
            <span className="text-sm font-semibold text-nlp-forest">NLP Master Practitioner Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-nlp-forest mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Go Beyond Practitioner. Develop Mastery.
          </h1>
          <p className="text-lg md:text-xl text-nlp-taupe mb-8 max-w-2xl mx-auto leading-relaxed">
            12 Days of Advanced NLP, Time Line Therapy®, Hypnosis, Coaching, Personal Breakthrough Work — Plus the New Model of NLP & NLP OS™
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' })} className="gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full">
              Explore the Curriculum
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button variant="secondary" size="lg" onClick={() => document.getElementById('bonus')?.scrollIntoView({ behavior: 'smooth' })} className="gap-2 bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full">
              Founding Member Bonus
            </Button>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>You've learned the foundations. You've learned powerful techniques. You've begun understanding how language, beliefs, emotions, internal representations and unconscious patterns shape human experience.</p>
            <p>But Practitioner was never meant to be the end of the journey. It was the foundation.</p>
            <p>Master Practitioner is where we go deeper. Deeper into values. Deeper into language. Deeper into unconscious patterns. Deeper into strategies and Meta Programs. Deeper into Time Line Therapy® and hypnosis. And deeper into your ability to recognize how all of these patterns interact within a human being.</p>
            <p>But we're also going somewhere traditional Master Practitioner training hasn't gone. We'll take what you're learning beyond the individual client and explore how the New Model of NLP and NLP OS™ can be applied to businesses, teams and organizations.</p>
            <p className="text-nlp-forest font-semibold text-xl pt-2">Welcome to your next level.</p>
          </div>
        </div>
      </section>

      {/* From Techniques to Mastery */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <span className="text-sm font-semibold text-nlp-forest">From NLP Techniques to NLP Mastery</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            At Practitioner level, you learned the patterns, models and techniques.
          </h2>
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>At Master Practitioner level, the question begins to change.</p>
            <p>Instead of simply asking:</p>
            <p className="text-nlp-forest font-semibold text-xl pl-6 border-l-4 border-nlp-gold">"Which NLP technique should I use?"</p>
            <p>you begin asking:</p>
            <p className="text-nlp-forest font-semibold text-xl pl-6 border-l-4 border-nlp-gold">"What is actually creating and maintaining this person's experience?"</p>
            <p>What values are driving them? What conflicts exist between those values? What Meta Programs influence how they perceive the world? What strategies are operating outside conscious awareness? What does their language reveal? What beliefs and decisions are involved? How are these patterns interacting?</p>
            <p>And most importantly:</p>
            <p className="text-nlp-forest font-bold text-xl">Where is the leverage point for change?</p>
            <p>That is a different level of thinking.</p>
            <p className="text-nlp-forest font-semibold text-xl pt-2">Mastery isn't about accumulating more techniques. It's about developing the flexibility to understand the person in front of you and respond appropriately.</p>
          </div>
        </div>
      </section>

      {/* Two Certifications */}
      <section id="certifications" className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-bg rounded-full mb-4">
              <Award className="w-4 h-4 text-nlp-gold-dark" />
              <span className="text-sm font-semibold text-nlp-forest">Two Advanced Certifications</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Two Certifications. One Advanced Professional Journey.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-nlp-bg rounded-4xl p-8 shadow-soft">
              <div className="w-14 h-14 rounded-full bg-nlp-forest flex items-center justify-center mb-6">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-nlp-forest mb-3">Certified Master NLP Practitioner</h3>
              <p className="text-nlp-taupe leading-relaxed">
                Develop advanced capability across NLP, Time Line Therapy®, hypnosis, coaching, language, values, strategies, Meta Programs, modelling and personal change.
              </p>
            </div>
            <div className="bg-nlp-bg rounded-4xl p-8 shadow-soft">
              <div className="w-14 h-14 rounded-full bg-nlp-olive flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-nlp-forest mb-3">Certified Master New Model of NLP Practitioner</h3>
              <p className="text-nlp-taupe leading-relaxed">
                Learn how to integrate advanced NLP with the New Model of NLP and NLP OS™—and begin expanding its application beyond individual coaching into businesses, teams and organizations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Master */}
      <section id="curriculum" className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <span className="text-sm font-semibold text-nlp-forest">What You'll Master</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Nine Advanced Modules of Mastery
            </h2>
          </div>
          <div className="space-y-8">
            {masteryModules.map((mod, i) => {
              const Icon = mod.icon;
              return (
                <div key={i} className="bg-nlp-cream rounded-4xl p-8 md:p-10 shadow-soft hover:shadow-soft-lg transition-shadow duration-300">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-full bg-nlp-forest flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold text-nlp-forest mb-4">{mod.title}</h3>
                      <p className="text-nlp-taupe leading-relaxed mb-4">{mod.body}</p>
                      <p className="text-nlp-taupe leading-relaxed">{mod.detail}</p>
                      {mod.pullquote && (
                        <p className="mt-6 text-lg text-nlp-forest font-semibold italic border-l-4 border-nlp-gold pl-5">
                          {mod.pullquote}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* From Individual to System */}
      <section className="py-20 px-4 md:px-8 bg-nlp-forest text-nlp-bg">
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            And Then We Take NLP Somewhere New.
          </h2>
          <p className="text-lg md:text-xl text-nlp-bg/80 mb-4 leading-relaxed">
            Most NLP training has traditionally focused on one person working with another person. And that's extraordinarily valuable.
          </p>
          <p className="text-lg md:text-xl text-nlp-bg/80 mb-4 leading-relaxed">
            But what happens when you take the same understanding of human patterns and begin looking at a team, a leadership group, a business or an organization?
          </p>
          <p className="text-xl md:text-2xl text-nlp-gold-light font-semibold pt-4">
            That's where the New Model of NLP becomes particularly powerful.
          </p>
        </div>
      </section>

      {/* New Model of NLP */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <span className="text-sm font-semibold text-nlp-forest">Master the New Model of NLP</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            NLP OS™ for Business, Teams & Organizations
          </h2>
          <p className="text-lg text-nlp-taupe mb-8 leading-relaxed">
            This is where your second certification begins opening entirely new possibilities. You'll explore how the New Model of NLP and NLP OS can be applied beyond traditional one-to-one coaching.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {orgApplications.map((app, i) => {
              const Icon = app.icon;
              return (
                <div key={i} className="bg-nlp-cream rounded-4xl p-6 shadow-soft">
                  <div className="w-12 h-12 rounded-full bg-nlp-olive flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-nlp-forest mb-2">{app.title}</h3>
                  <p className="text-nlp-taupe leading-relaxed">{app.body}</p>
                </div>
              );
            })}
          </div>
          <p className="text-lg text-nlp-taupe mt-8 leading-relaxed">
            This gives you the opportunity to think differently about where your NLP expertise could eventually be applied.
          </p>
        </div>
      </section>

      {/* What Could Master Practitioner Do For You */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              What Could Master Practitioner Do For You?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-nlp-bg rounded-4xl p-8 shadow-soft">
              <div className="w-14 h-14 rounded-full bg-nlp-forest flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-nlp-forest mb-3">Personally</h3>
              <p className="text-nlp-taupe leading-relaxed">
                This isn't simply professional education. You'll be working on yourself throughout the journey. You'll examine your own values, beliefs, patterns, strategies, language, goals, decisions and unconscious processes. And you'll experience your own Personal Breakthrough. For many people, that personal journey alone can become one of the most valuable parts of Master Practitioner.
              </p>
            </div>
            <div className="bg-nlp-bg rounded-4xl p-8 shadow-soft">
              <div className="w-14 h-14 rounded-full bg-nlp-olive flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-nlp-forest mb-3">For Your Clients</h3>
              <p className="text-nlp-taupe leading-relaxed">
                Clients don't arrive neatly packaged around an NLP technique. A presenting problem can involve beliefs, values, emotions, decisions, language, identity, strategies, relationships, environment and unconscious patterns all interacting simultaneously. Master Practitioner gives you more ways to understand that complexity. Instead of thinking "Which technique fixes this?" you can increasingly ask "What is happening here, and what does this individual actually need?"
              </p>
            </div>
            <div className="bg-nlp-bg rounded-4xl p-8 shadow-soft">
              <div className="w-14 h-14 rounded-full bg-nlp-gold-dark flex items-center justify-center mb-6">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-nlp-forest mb-3">For Your Business</h3>
              <p className="text-nlp-taupe leading-relaxed">
                Greater capability can create greater possibilities. Depending on your professional background, experience, market and scope of practice, Master Practitioner can help you develop the skills to create deeper and broader services. That could include advanced coaching engagements, Personal Breakthrough experiences, premium transformation programs, workshops, leadership development, team development, business consulting, organizational work and professional presentations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 12 Live Training Days */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <Clock className="w-4 h-4 text-nlp-gold-dark" />
            <span className="text-sm font-semibold text-nlp-forest">12 Live Training Days</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            But We're Not Going to Rush This.
          </h2>
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>Traditional advanced training is often delivered in one intensive block. There's value in immersion. But there's also enormous value in integration.</p>
            <p>That's why we're taking a different approach. Your Master Practitioner journey will be delivered across:</p>
            <div className="bg-nlp-cream rounded-4xl p-8 my-6 text-center">
              <p className="text-2xl font-bold text-nlp-forest mb-2">12 Live Training Days</p>
              <p className="text-lg text-nlp-taupe">Approximately: 2 Days Per Month × 6 Months</p>
            </div>
            <p>That gives you something an intensive course cannot. Time.</p>
            <p>Time to learn something. Time to practice it. Time to use it. Time to experience it. Time to work with NLP OS. Time to notice what you don't yet understand.</p>
            <p>And then... come back. Ask questions. Go deeper. Learn the next level. Apply it again.</p>
            <p className="text-nlp-forest font-semibold text-xl pt-2">
              Instead of attending another training and disappearing afterwards... we're going to spend six months developing mastery together.
            </p>
          </div>
        </div>
      </section>

      {/* More Than a Training */}
      <section className="py-20 px-4 md:px-8 bg-nlp-forest text-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            More Than a Training. A Community.
          </h2>
          <div className="space-y-4 text-lg text-nlp-bg/80 leading-relaxed">
            <p>There's another reason we're doing it this way. We don't want NLP OS™ to become another piece of software you subscribe to. That's not the vision.</p>
            <p>We want to create a community of professionals who continue learning together, practicing together, developing together, building businesses together, sharing experiences and raising the standard of the work we collectively bring into the world.</p>
            <p>Technology should support the practitioner. But ultimately... The practitioner is the difference.</p>
            <p className="text-nlp-gold-light font-semibold text-xl pt-2">That's why we're investing in you.</p>
          </div>
        </div>
      </section>

      {/* Journey Includes */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <Trophy className="w-4 h-4 text-nlp-gold-dark" />
              <span className="text-sm font-semibold text-nlp-forest">Your Master Practitioner Journey Includes</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Everything You'll Experience
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
            {journeyIncludes.map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-nlp-cream rounded-2xl p-4">
                <Check className="w-5 h-5 text-nlp-olive flex-shrink-0 mt-0.5" />
                <span className="text-nlp-forest font-medium text-sm">{item}</span>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-nlp-forest text-nlp-bg rounded-4xl p-8 text-center">
              <GraduationCap className="w-10 h-10 text-nlp-gold-light mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Certified Master NLP Practitioner</h3>
            </div>
            <div className="bg-nlp-olive text-nlp-bg rounded-4xl p-8 text-center">
              <Sparkles className="w-10 h-10 text-nlp-gold-light mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Certified Master New Model of NLP Practitioner</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond Mastery */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-bg rounded-full mb-6">
            <span className="text-sm font-semibold text-nlp-forest">And There's Another Level Beyond Mastery...</span>
          </div>
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>For some of you, Master Practitioner will represent exactly where you want to take your NLP journey.</p>
            <p>For others... it may awaken something else. The desire not only to practice NLP... but eventually to teach it.</p>
            <p>In the future, qualifying Master Practitioners may have the opportunity to continue into advanced Trainer pathways including NLP Trainer, New Model of NLP Trainer and potentially Licensed NLP OS™ Trainer.</p>
            <p>But that's for another day. Right now, there's one thing to focus on.</p>
            <p className="text-nlp-forest font-bold text-xl">Becoming a Master Practitioner.</p>
          </div>
        </div>
      </section>

      {/* Founding Member Bonus */}
      <section id="bonus" className="py-20 md:py-28 px-4 md:px-8 bg-nlp-forest text-nlp-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nlp-gold/10 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-nlp-olive/10 rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />
        <div className="max-w-[900px] mx-auto relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-gold/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-nlp-gold-light" />
              <span className="text-sm font-semibold text-nlp-gold-light">And Now For Something We Didn't Tell You.</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              Your Unannounced Founding Member Bonus
            </h2>
          </div>
          <div className="space-y-4 text-lg text-nlp-bg/80 leading-relaxed mb-10">
            <p>When you upgraded to your Annual or Lifetime NLP OS Founding Membership... this wasn't included. We didn't promise it. You didn't buy it. And we deliberately didn't tell you about it.</p>
            <p>Because we wanted to do something extraordinary for the people who chose to become Annual or Lifetime Founding Members at the very beginning.</p>
            <p className="text-nlp-gold-light font-semibold text-xl">You made an commitment to becoming part of NLP OS. And we want to recognize that by investing even further in your development.</p>
          </div>
          <div className="bg-nlp-bg rounded-4xl p-8 md:p-12 shadow-soft-lg">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                The Complete NLP Master Practitioner Journey
              </h3>
              <p className="text-nlp-taupe">12 Live Training Days • Two Advanced Certifications • Six Months of Advanced Professional Development • Your Own Personal Breakthrough Experience • Advanced New Model of NLP & NLP OS™ Training • Business, Team & Organizational Applications</p>
            </div>
            <div className="flex flex-col items-center gap-2 mb-8">
              <p className="text-lg text-nlp-taupe line-through">Normal Tuition: $2,997</p>
              <p className="text-sm text-nlp-taupe">Your Additional Tuition as an Eligible NLP OS Annual or Lifetime Founding Member:</p>
              <p className="text-5xl md:text-6xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>$0</p>
            </div>
            <div className="bg-nlp-cream rounded-2xl p-6 text-center">
              <p className="text-nlp-forest font-semibold text-lg">It's included with your Founding Membership.</p>
              <p className="text-nlp-taupe mt-2">Not because the training has no value. Quite the opposite. We're doing it because you have value to what we're building.</p>
            </div>
          </div>
          <div className="text-center mt-10">
            <p className="text-2xl md:text-3xl font-bold text-nlp-gold-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Your Founding Membership Just Became Even More Valuable.
            </p>
            <p className="text-lg text-nlp-bg/70 leading-relaxed max-w-2xl mx-auto">
              Our ambition isn't simply to create another NLP training company. And it isn't simply to create another coaching software platform. We believe there is an opportunity to bring together the foundations of Classical NLP + advanced professional training + the New Model of NLP + modern AI-powered technology through NLP OS™ + a committed professional community into something different.
            </p>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Welcome to Master Practitioner.
          </h2>
          <p className="text-lg md:text-xl text-nlp-taupe mb-10 leading-relaxed">
            Your Practitioner training gave you the foundation. Now let's develop mastery.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/home')}
            className="gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
          >
            Back to Home
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 bg-nlp-forest-dark text-nlp-bg/60">
        <div className="max-w-[1216px] mx-auto text-center">
          <NLPLogo className="h-10 mx-auto mb-4" />
          <p className="text-sm">NLP OS™ — Classical NLP. Modern Architecture. AI-Powered Application.</p>
        </div>
      </footer>
    </div>
  );
}
